import { ACTION_TYPE, JobBuilderContext } from 'context/jobBuilderContext';
import { ACTION_TYPE as APP_ACTION_TYPE, AppContext } from 'context/appContext';
import {
  EntityTypes,
  JobType,
  Property,
  PropertyGroup,
  DoNotTowRequest,
  Location,
  CreateJobBody,
  PanelRootContext,
  Tenant,
} from 'types';
import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { addressesMatch, getFormattedUserName } from 'utils';
import {
  useFetcher,
  useLoaderData,
  useNavigate,
  useOutletContext,
  useRouteLoaderData,
} from 'react-router';

import DntDialogReason from 'pages/jobs/create/DNTDialog';
import { FileContext } from 'context/fileUploadContext';
import GeneralStep from './GeneralStep';
import LocationStep from './LocationStep';
import NotesStep from './NotesStep';
import { ValidatorForm } from 'react-material-ui-form-validator';
import VehicleStep from './VehicleStep';
import { useTranslation } from 'react-i18next';
import ReactGA from 'react-ga4';
import DetailsHeader from 'components/layout/DetailsHeader';
import FormHeaderBar from 'components/layout/FormHeaderBar';
import FormFrame from 'components/layout/FormFrame';
import SettingsSection from 'components/layout/SettingsSection';
import { useFetcherKey } from 'hooks/useFetcherKey';
import { ScrollableFormContainer } from 'components/layout/ScrollableContainer';
import { logError } from 'utils';
import { JobActionHandler, JobsActionResponse } from '../jobsAction';
import { JobsCreatePropertyLoaderData } from './jobPropertyDataLoader';
import DigitalSignatureWidget from 'components/widgets/DigitalSignatureWidget';
import AuthService from 'utils/AuthService';
import { getTenantPropertyGroups } from 'api/propertyGroup';
import { DashboardLoaderData } from 'pages/dashboard/Dashboard';
import { ROUTES, routeWithParams } from 'router/routes';

const JobsCreate = (): ReactNode => {
  const { t } = useTranslation();
  const { entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup | Tenant>>();
  const { state: appState, dispatch: appDispatch } = useContext(AppContext);
  const { state: jobState, dispatch } = useContext(JobBuilderContext);
  const { clearCompleted, files } = useContext(FileContext);
  const [showDialog, setShowDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  const displayToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const dashboardLoaderData = useRouteLoaderData<DashboardLoaderData>(
    ROUTES.DASHBOARD,
  ) as DashboardLoaderData | null;
  const loaderData = useLoaderData<JobsCreatePropertyLoaderData>();

  useEffect(() => {
    // set mobile header text
    appDispatch({
      type: APP_ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('buttons.create', { name: t('job.label', { ns: 'entities' }) }),
    });

    if (displayMode === 'property' && entity) {
      const p = entity as Property;
      dispatch({ type: ACTION_TYPE.SET_PROPERTY, payload: p });
      dispatch({ type: ACTION_TYPE.SET_PROPERTY_LIST_OPTIONS, payload: [p] });
      dispatch({
        type: ACTION_TYPE.SET_PROPERTY_GROUPS,
        payload: p.propertyGroups || [],
      });
      dispatch({
        type: ACTION_TYPE.SET_LOCATION,
        payload: p.location,
      });
      if (loaderData?._violations) {
        loaderData._violations.then((violations) => {
          dispatch({
            type: ACTION_TYPE.SET_VIOLATION_LIST_OPTIONS,
            payload: violations,
          });
        });
      }
    } else if (displayMode === 'group' && entity) {
      const pg = entity as PropertyGroup;
      dispatch({ type: ACTION_TYPE.SET_PROPERTY_GROUPS, payload: [pg] });
      dispatch({
        type: ACTION_TYPE.SET_PROPERTY_LIST_OPTIONS,
        payload: pg.properties || [],
      });
    } else if (displayMode === 'tenant' && entity) {
      const properties = dashboardLoaderData?.properties || [];
      dispatch({
        type: ACTION_TYPE.SET_PROPERTY_LIST_OPTIONS,
        payload: properties,
      });
      // Load full property groups for job creation (needs nested properties for filtering)
      getTenantPropertyGroups(appState.tenant.id).then((groups) => {
        dispatch({
          type: ACTION_TYPE.SET_PROPERTY_GROUPS,
          payload: groups,
        });
      });
    }

    return () => {
      // if the user navigates away from the page, clear the file upload context of anything not in progress
      clearCompleted();
    };
  }, [entity]);

  // leaving the create form returns to the jobs list for the context it was
  // opened from. navigate(-1) is not safe here: any stray popstate listener or
  // replaced history entry sends the user somewhere unrelated.
  const jobsListRoute = useMemo(() => {
    if (displayMode === 'property') {
      return routeWithParams(ROUTES.PROPERTY_JOBS, [
        appState.tenant.id,
        entity.id,
      ]);
    }
    if (displayMode === 'group') {
      return routeWithParams(ROUTES.PROPERTY_GROUP_JOBS, [
        appState.tenant.id,
        entity.id,
      ]);
    }
    return routeWithParams(ROUTES.JOBS, [appState.tenant.id]);
  }, [displayMode, appState.tenant.id, entity.id]);

  const handleBack = () => {
    navigate(jobsListRoute);
  };

  const response = fetcher.data as JobsActionResponse | null;
  useEffect(() => {
    if (response) {
      if (response.error) {
        logError(response.error);
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: t('create_error', {
            name: t('job.label', { ns: 'entities' }),
            error: response.error,
            ns: 'toasts',
          }),
        });
      } else if (response.dntMsg) {
        setDialogMessage(response.dntMsg || 'Unknown error');
        setShowDialog(true);
      } else {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: t('create_success', {
            name: t('job.label', { ns: 'entities' }),
            ns: 'toasts',
          }),
        });

        ReactGA.gtag('event', 'job_create_success', {
          event_category: 'job',
          event_label: 'job_create_success',
          job_type: jobState.jobType,
          property: jobState.property?.name,
          tenant_id: appState.tenant.id,
          value: 1,
        });

        // replace so the browser back button cannot re-enter the form that was
        // just submitted
        navigate(jobsListRoute, { replace: true });
      }
    }
  }, [fetcher.data]);

  const requireSignature = useMemo(() => {
    let required = false;
    const jobTypesNeedingSignature =
      jobState.property?.data?.settings?.requirePMApproval || [];
    if (jobTypesNeedingSignature.length) {
      required = jobTypesNeedingSignature.some(
        (jt: JobType) => jobState.jobType === jt,
      );
    }
    return required;
  }, [jobState.jobType, jobState.property]);

  const disableSubmit = useMemo(() => {
    let disabled = false;
    if (!jobState.property) {
      disabled = true;
    }
    if (AuthService.IsPM() && requireSignature && !jobState.signature) {
      disabled = true;
    }
    if (!jobState.vin && !jobState.plateNumber && !jobState.plateState) {
      disabled = true;
    }
    return disabled;
  }, [
    requireSignature,
    jobState.property,
    jobState.signature,
    jobState.vin,
    jobState.plateNumber,
    jobState.plateState,
  ]);

  const handleSubmit = () => {
    if (!jobState.property || !jobState.jobType) {
      return;
    }

    // get file keys
    const keys = [] as Array<string>;
    files.forEach((file) => {
      if (
        file.metadata.entityType === EntityTypes.Job &&
        file.metadata.entityID === 0 &&
        file.metadata.fileKey
      ) {
        keys.push(file.metadata.fileKey);
      }
    });

    // leaving location as null will default to property location
    let location: Location | null = null;
    if (
      !addressesMatch(jobState.property.location, {
        unit: jobState.unit || '',
        street: jobState.street || '',
        city: jobState.city || '',
        postalCode: jobState.postalCode || '',
      })
    ) {
      // Set id to 0 to create new location
      location = {
        id: 0,
        street: jobState.street || '',
        unit: jobState.unit || '',
        city: jobState.city || '',
        stateCode: jobState.stateCode || '',
        postalCode: jobState.postalCode || '',
        country: jobState.country || '',
      };
    }

    const newJob: CreateJobBody = {
      propertyID: jobState.property?.id || 0,
      tenantID: appState.tenant.id,
      createdByUserID: appState.user.id,
      createdByUserName: getFormattedUserName(appState.user),
      location: location,
      type: jobState.jobType,
      contactPhone: jobState.contactPhone,
      requestTime: jobState.requestTime?.toJSON() || null,
      notes: jobState.notes,
      landmark: jobState.landmark,
      vehicle: {
        vin: jobState.vin || '',
        make: jobState.make || '',
        model: jobState.model || '',
        color: jobState.color || '',
        trim: jobState.trim || '',
        plate: {
          number: jobState.plateNumber || '',
          state: jobState.plateState || '',
        },
      },
      violations: jobState.violations.map((v) => ({
        id: v.id,
        name: v.name,
      })),
      fileKeys: keys,
      signatureFileKey: jobState.signature?.fileKey,
      statuses: [], // the server will set the initial status
    };

    if (jobState.year && newJob.vehicle) {
      newJob.vehicle.year = Number(jobState.year);
    }

    if (jobState.plateExp && newJob.vehicle?.plate) {
      newJob.vehicle.plate.exp = jobState.plateExp.getTime();
    }

    const cleanJobData = Object.fromEntries(
      Object.entries(newJob).filter(([_, value]) => value !== undefined),
    );

    const dntRequest: DoNotTowRequest = {
      property: jobState.property?.id || 0,
      time: jobState.requestTime
        ? Math.floor(jobState.requestTime.getTime() / 1000)
        : Math.floor(Date.now() / 1000),
      vin: jobState.vin || '',
      plate: {
        number: jobState.plateNumber || '',
        state: jobState.plateState || '',
        exp: jobState.plateExp || null,
      },
      violations: jobState.violations.map((v) => ({
        id: v.id,
        name: v.name,
      })),
      operation: jobState.jobType,
    };

    let handler = JobActionHandler.CREATE;
    if (showDialog) {
      handler = JobActionHandler.OVERRIDE_DNT;
      setShowDialog(false);
    }

    fetcher.submit(
      {
        jobBody: JSON.stringify(cleanJobData),
        dntBody: JSON.stringify(dntRequest),
        tenantID: appState.tenant.id,
        handler: handler,
      },
      {
        method: 'POST',
        encType: 'application/json',
      },
    );

    ReactGA.gtag('event', 'job_review_submit', {
      event_category: 'job',
      event_label: 'job_review_submit',
      sent_signature: jobState.signature ? true : false,
      sent_files: keys.length,
      value: 1,
    });
  };

  const handleSignatureChange = (applied: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_SIGNATURE,
      payload: applied ? appState.signature : null,
    });
  };

  const minimumVehicleRequirementsMet = useMemo(() => {
    const hasVin = !!jobState.vin && jobState.vin.length >= 5;
    const hasPlate = !!jobState.plateNumber && !!jobState.plateState;
    return hasVin || hasPlate;
  }, [jobState.vin, jobState.plateNumber, jobState.plateState]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormHeaderBar />
      {showDialog && (
        <DntDialogReason
          open={showDialog}
          setOpen={setShowDialog}
          setReason={setReason}
          reason={reason}
          handleResubmit={handleSubmit}
          dialogMessage={dialogMessage}
        />
      )}
      <FormFrame loading={fetcher.state !== 'idle'}>
        <DetailsHeader
          title={t('buttons.create', {
            name: t('job.label', { ns: 'entities' }),
          })}
          onCancel={handleBack}
          cancelButtonLabel={t('cancel')}
          submitLabel={t('buttons.create', {
            name: t('job.label', { ns: 'entities' }),
          })}
          disableSubmit={disableSubmit}
        />
        <ScrollableFormContainer maxHeight={`calc(100dvh - 324px)`}>
          <SettingsSection title={t('steps.general', { ns: 'jobs' })}>
            <GeneralStep fetcherKey={fetcherKey} />
          </SettingsSection>

          <SettingsSection title={t('steps.location', { ns: 'jobs' })}>
            <LocationStep />
          </SettingsSection>

          <SettingsSection
            title={t('steps.vehicle', { ns: 'jobs' })}
            subtitle={!minimumVehicleRequirementsMet ? t('helpers.vehicle_step', { ns: 'jobs' }) : ''}
            subtitleError={
              !minimumVehicleRequirementsMet
            }
          >
            <VehicleStep />
          </SettingsSection>

          <SettingsSection
            title={t('steps.notes_and_files', { ns: 'jobs' })}
            divider={requireSignature}
          >
            <NotesStep />
          </SettingsSection>

          {requireSignature && (
            <SettingsSection
              title={t('signature.title', { ns: 'userSettings' })}
              subtitle={t('helpers.signature_step', { ns: 'jobs' })}
              divider={false}
            >
              <DigitalSignatureWidget
                onSignatureApplied={handleSignatureChange}
              />
            </SettingsSection>
          )}
        </ScrollableFormContainer>
      </FormFrame>
    </ValidatorForm>
  );
};

export default JobsCreate;
